import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Loader2, Mail, Send, ShieldAlert } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

const STATUS = {
  sent: { label: "送信済", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  failed: { label: "失敗", cls: "bg-red-50 text-red-700 border-red-200" },
  dry_run: { label: "プレビュー", cls: "bg-gray-100 text-gray-600 border-gray-200" },
};

export default function Emails() {
  const { user } = useAuth();
  const { t } = useI18n();
  const isAdmin = user?.role === "company_admin" || user?.role === "system_admin";
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/reminders/emails"); setEmails(data); }
    catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [load, isAdmin]);

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/reminders/run", { dry_run: false });
      toast.success(`${t("リマインダーを送信しました")} (${data.sent})`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "送信に失敗しました");
    } finally { setRunning(false); }
  };

  const fmt = (v) => { const d = new Date(v); return isNaN(d) ? v : d.toLocaleString(); };

  if (!isAdmin) {
    return (
      <div className="animate-fade-in">
        <PageHeader title="メール通知" />
        <div className="bg-white border border-border rounded-sm py-16 text-center text-muted-foreground">
          <ShieldAlert className="w-10 h-10 mx-auto mb-3 text-gray-300" />{t("この画面へのアクセス権限がありません")}
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="メール通知"
        subtitle="契約更新リマインダーの送信履歴"
        actions={
          <Button onClick={run} disabled={running} className="bg-brand hover:bg-brand-hover text-white" data-testid="run-reminders">
            {running ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}{t("リマインダーを今すぐ送信")}
          </Button>
        }
      />

      <div className="bg-brand-soft border border-orange-200 rounded-sm p-4 mb-6 flex items-start gap-3 text-sm">
        <Mail className="w-5 h-5 text-brand shrink-0 mt-0.5" />
        <div className="text-[#7a3800]">{t("会社管理者・管理担当ユーザーへ90/60/30日前に自動送信")}</div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>
      ) : (
        <div className="border border-border bg-white rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3">{t("送信日時")}</th>
                  <th className="px-4 py-3">{t("宛先")}</th>
                  <th className="px-4 py-3">{t("件名")}</th>
                  <th className="px-4 py-3">{t("状態")}</th>
                </tr>
              </thead>
              <tbody>
                {emails.map((e, i) => {
                  const s = STATUS[e.status] || STATUS.dry_run;
                  return (
                    <tr key={e.id || i} className="border-b last:border-0 hover:bg-gray-50" data-testid={`email-${i}`}>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmt(e.created_at)}</td>
                      <td className="px-4 py-3">{e.to}</td>
                      <td className="px-4 py-3">{e.subject}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-sm border text-xs font-medium ${s.cls}`}>{t(s.label)}</span>
                      </td>
                    </tr>
                  );
                })}
                {emails.length === 0 && <tr><td colSpan={4} className="px-4 py-12 text-center text-muted-foreground">{t("履歴がありません")}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
